import { OrdersSegment } from './gameDay-data.segmentations';
import { trancheExtractors } from '../mappers/gameDay.extractors';
import {
  ordersSegmentsToTrancheGroups,
  TrancheGroup,
  Part
} from './gameDay-data.transformations';
import {
  calculateSegments,
  setAllSegments,
  setAllTrancheGroups
} from './gameDay-data-common.calculations';
import {
  AccountManager,
  GameDayViewType,
  GamedayFiltersState
} from '../../state/data/state';
import { orderIsLowPriorityType } from './gameDay-data-orders.calculation';
import { GameDayModel } from '../../models/gameday-model';
import { cloneDeep } from 'lodash';

export interface GameDayCalculatedData extends GameDayModel.RawData {
  segments: OrdersSegment<any>[];
  segmentsMap: Map<GameDayViewType, OrdersSegment<any>[]>;
  trancheGroups: TrancheGroup[];
  trancheGroupsMap: Map<GameDayViewType, TrancheGroup[]>;
}

export const MANAGER = 'MANAGER';
export const ACCOUNT = 'ACCOUNT';

export function filterBySeriesIds(
  data: GameDayModel.RawData,
  seriesIds: string[]
): GameDayModel.RawData {
  let deal = cloneDeep(data.deal);
  let orders = data.orders;
  let maturities = deal.maturities;

  if (!seriesIds) {
    return data;
  }
  maturities = maturities.filter(
    (maturity) => seriesIds.indexOf(maturity.seriesId) > -1
  );
  orders = orders.filter((order) => seriesIds.indexOf(order.seriesId) > -1);
  deal = { ...deal, maturities };
  return { deal, orders };
}

export function filterByStock(
  data: GameDayModel.RawData,
  ignoreStocks: boolean
): GameDayModel.RawData {
  const deal = data.deal;
  let orders = data.orders;
  if (!ignoreStocks) {
    return data;
  }
  orders = orders.filter(
    (order) => order.priority !== GameDayModel.OrderPriority.stock
  );
  return { deal, orders };
}

function filterBySealedBid(
  data: GameDayModel.RawData,
  isSealedBid: boolean
): GameDayModel.RawData {
  let deal = data.deal;
  let orders = data.orders;
  let maturities = deal.maturities;
  if (!isSealedBid) {
    return data;
  }
  orders = orders.filter(
    (order) =>
      !(
        Object.prototype.hasOwnProperty.call(order.maturity, 'isSealedBid') &&
        order.maturity.isSealedBid
      )
  );
  maturities = maturities.filter(
    (maturity) =>
      !(
        Object.prototype.hasOwnProperty.call(maturity, 'isSealedBid') &&
        maturity.isSealedBid
      )
  );
  deal = { ...deal, maturities };
  return { deal, orders };
}

export function searchByManagerAccount(
  data: GameDayModel.RawData,
  searchByAccountManager: AccountManager
): GameDayModel.RawData {
  const deal = data.deal;
  let orders = data.orders;
  if (!searchByAccountManager) {
    return data;
  }
  switch (searchByAccountManager.type) {
    case MANAGER:
      orders = orders.filter(
        (order) => order.syndicateDepartment.id === searchByAccountManager.id
      );
      break;
    case ACCOUNT:
      orders = orders.filter(
        (order) => order.accountName === searchByAccountManager.name
      );
      break;
    default:
      break;
  }
  return { deal, orders };
}

export function filterGameDayData(
  rawData: GameDayModel.RawData,
  params: GamedayFiltersState
) {
  const rawDataClone = cloneDeep(rawData);
  if (
    !rawDataClone ||
    !rawDataClone.deal ||
    !rawDataClone.orders ||
    !rawDataClone.deal.maturities
  ) {
    return rawDataClone;
  }

  rawDataClone.deal.maturities = rawDataClone.deal.maturities.filter(
    (maturity: GameDayModel.Maturity) => !maturity.deletedInd
  );

  let data = filterBySeriesIds(rawDataClone, params.seriesId);
  data = filterByStock(data, params.ignoreStocks);
  data = filterBySealedBid(data, params.isSealedBid);
  return data;
}

export function calculateDataWithAppliedLegends(
  data: GameDayModel.RawData,
  viewType: GameDayViewType,
  legendFilters: string[]
): GameDayCalculatedData {
  const legendFilter = legendFilters.reduce(
    (prev: Record<string, unknown>, filter: string) => ({
      ...prev,
      [filter]: filter
    }),
    {}
  );
  const searchedData: GameDayCalculatedData = calculateData(data, viewType);
  const ordersFilter = filterByLegend(searchedData, legendFilter);
  const orders: GameDayModel.Order[] = searchedData.orders.filter(
    (order) => !ordersFilter[order.id]
  );

  return calculateData({ ...data, orders }, viewType);
}

export function getOrderTypeName(order: GameDayModel.Order): string {
  switch (order.priority) {
    case GameDayModel.OrderPriority.institutional:
      return GameDayModel.INSTITUTIONAL;
    case GameDayModel.OrderPriority.stock:
      return GameDayModel.STOCK;
    case GameDayModel.OrderPriority.retail:
      return order.category &&
        order.category.name &&
        order.category.code &&
        !orderIsLowPriorityType(order)
        ? order.category.name
        : GameDayModel.RETAIL;
    default:
      return '';
  }
}

export function calculateData(
  data: GameDayModel.RawData,
  viewType: GameDayViewType
): GameDayCalculatedData {
  const segmentsMap = new Map<GameDayViewType, OrdersSegment<any>[]>();
  const trancheGroupsMap = new Map<GameDayViewType, TrancheGroup[]>();

  const segments: OrdersSegment<any>[] = calculateSegments(data, viewType);
  segmentsMap.set(viewType, segments);
  const trancheGroups: TrancheGroup[] = ordersSegmentsToTrancheGroups(
    segments,
    trancheExtractors
  );
  trancheGroupsMap.set(viewType, trancheGroups);

  setAllSegments(data, segmentsMap, viewType);
  setAllTrancheGroups(segmentsMap, trancheGroupsMap, viewType);

  return { ...data, segments, segmentsMap, trancheGroups, trancheGroupsMap };
}

export function filterByLegend(
  { trancheGroups }: GameDayCalculatedData,
  legendFilters: Record<string, unknown>
): any {
  if (!legendFilters) {
    return [];
  }

  return trancheGroups
    .reduce((prev: Part[], { parts }: TrancheGroup) => [...prev, ...parts], [])
    .filter(({ id }: Part) => legendFilters[id])
    .reduce(
      (prev: GameDayModel.Order[], { orders }: Part) => [...prev, ...orders],
      []
    )
    .reduce(
      (prev: any, { id }: GameDayModel.Order) => ({
        ...prev,
        [id]: id
      }),
      {}
    );
}
